import React, { useState, useEffect } from 'react';
import { useShopPageProps } from '#app/layouts/ShopLayout';
import {
	Box,
	Typography,
	Button,
	Paper,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	IconButton,
	Tooltip,
	Card,
	CardContent,
	CardActions,
	Chip,
	Grid,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import PromptGeneratorDialog from '#app/components/PromptGeneratorDialog';
import HotPepperPromptGeneratorDialog from '#app/components/HotPepperPromptGeneratorDialog';
import S3Uploader from '#app/components/S3Uploader';
import { useNickFetch } from '#app/hooks/useNickFetch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HistoryIcon from '@mui/icons-material/History';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import SaveIcon from '@mui/icons-material/Save';

// 文字数選択肢
const LENGTH_OPTIONS = [
	{ value: 100, label: '100文字' },
	{ value: 200, label: '200文字' },
	{ value: 300, label: '300文字' },
	{ value: 400, label: '400文字' },
	{ value: 500, label: '500文字' },
	{ value: 600, label: '600文字' },
	{ value: 700, label: '700文字' },
	{ value: 800, label: '800文字' },
	{ value: 900, label: '900文字' },
	{ value: 1000, label: '1000文字' }
];

// 履歴の最大保持件数
const MAX_HISTORY_COUNT = 12;

// 履歴の型定義
type BlogGenerationHistory = {
	id: string;
	createdAt: string;
	characterPrompt: string;
	imageUrls: string[];
	bodyLength: number;
	generatedTitle: string;
	generatedBody: string;
	isFavorite?: boolean; // お気に入りフラグ（後方互換性のためオプショナル）
};

// ローカルストレージのキー
const HISTORY_STORAGE_KEY = 'ai_blog_generation_history';
const CHARACTER_PROMPT_STORAGE_KEY = 'ai_blog_character_prompt_temp';
const getCharacterPromptStorageKey = (shopId: string | number) =>
	`${CHARACTER_PROMPT_STORAGE_KEY}_${String(shopId)}`;

// 履歴を取得
const getHistory = (): BlogGenerationHistory[] => {
	try {
		const stored = localStorage.getItem(HISTORY_STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch (error) {
		console.error('Failed to load history:', error);
	}
	return [];
};

// 履歴を保存
const saveHistory = (history: BlogGenerationHistory[]) => {
	try {
		localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
	} catch (error) {
		console.error('Failed to save history:', error);
	}
};

// 履歴を追加
const addHistory = (item: Omit<BlogGenerationHistory, 'id' | 'createdAt'>) => {
	const history = getHistory();
	const newItem: BlogGenerationHistory = {
		...item,
		id: Date.now().toString(),
		createdAt: new Date().toISOString()
	};
	// 最新のものを先頭に追加
	let updatedHistory = [newItem, ...history];
	
	// お気に入りを優先的に保持（お気に入りは削除しない）
	const favorites = updatedHistory.filter(item => item.isFavorite);
	const nonFavorites = updatedHistory.filter(item => !item.isFavorite);
	
	// お気に入り + 非お気に入り（最大件数 - お気に入り数まで）
	const maxNonFavorites = Math.max(0, MAX_HISTORY_COUNT - favorites.length);
	const trimmedNonFavorites = nonFavorites.slice(0, maxNonFavorites);
	
	updatedHistory = [...favorites, ...trimmedNonFavorites];
	saveHistory(updatedHistory);
	return updatedHistory;
};

// 履歴を削除
const deleteHistory = (id: string) => {
	const history = getHistory();
	const updatedHistory = history.filter(item => item.id !== id);
	saveHistory(updatedHistory);
	return updatedHistory;
};

// お気に入りを切り替え
const toggleFavorite = (id: string) => {
	const history = getHistory();
	const updatedHistory = history.map(item => 
		item.id === id 
			? { ...item, isFavorite: !(item.isFavorite || false) }
			: item
	);
	saveHistory(updatedHistory);
	return updatedHistory;
};

// 性格プロンプトの一時保存
const saveCharacterPrompt = (prompt: string, shopId: string | number) => {
	try {
		localStorage.setItem(getCharacterPromptStorageKey(shopId), prompt);
		return true;
	} catch (error) {
		console.error('Failed to save character prompt:', error);
		return false;
	}
};

const loadCharacterPrompt = (shopId: string | number): string => {
	try {
		const scoped = localStorage.getItem(getCharacterPromptStorageKey(shopId));
		// 旧キーに保存されている場合の後方互換を確保
		if (scoped !== null) return scoped;
		return localStorage.getItem(CHARACTER_PROMPT_STORAGE_KEY) || '';
	} catch (error) {
		console.error('Failed to load character prompt:', error);
		return '';
	}
};

export default function AIBlogGenerator() {
	const { shop } = useShopPageProps();
	const [urlPromptDialogOpen, setUrlPromptDialogOpen] = useState<boolean>(false);
	const [hotpepperPromptDialogOpen, setHotpepperPromptDialogOpen] = useState<boolean>(false);
	
	// 1. AIの性格を設定
	const [characterPrompt, setCharacterPrompt] = useState<string>('');
	const [promptSaveMessage, setPromptSaveMessage] = useState<string>('');
	
	// 2. 画像をアップロード
	const [imageUrls, setImageUrls] = useState<string[]>([]);
	
	// 3. 生成設定
	const [bodyLength, setBodyLength] = useState<number>(500);
	
	// 4. ブログ内容を生成
	const [generatedTitle, setGeneratedTitle] = useState<string>('');
	const [generatedBody, setGeneratedBody] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string>('');
	
	// 履歴
	const [history, setHistory] = useState<BlogGenerationHistory[]>([]);
	const [selectedHistory, setSelectedHistory] = useState<BlogGenerationHistory | null>(null);
	
	const { nickFetch } = useNickFetch();
	
	// 性格プロンプトの一時保存分を読み込む
	useEffect(() => {
		const savedPrompt = loadCharacterPrompt(shop.id);
		setCharacterPrompt(savedPrompt);
		setPromptSaveMessage('');
	}, [shop.id]);

	// 履歴を読み込む
	useEffect(() => {
		setHistory(getHistory());
	}, []);

	const handleImageUpload = (url: string) => {
		if (imageUrls.length >= 4) {
			setError('画像は最大4枚までアップロードできます');
			return;
		}
		setImageUrls([...imageUrls, url]);
		setError('');
	};

	const handleDeleteImage = (index: number) => {
		const newImageUrls = imageUrls.filter((_, i) => i !== index);
		setImageUrls(newImageUrls);
	};

	const handleGenerate = async () => {
		if (imageUrls.length === 0) {
			setError('画像をアップロードしてください');
			return;
		}

		setLoading(true);
		setError('');

		try {
			const res = await nickFetch("/generate_blog_from_image/", {
				shop_id: shop.id,
				image_urls: imageUrls,
				body_length: bodyLength,
				style: '', // 生成スタイルは使わない
				custom_prompt: characterPrompt // AIの性格をカスタムプロンプトとして使用
			});

			if (!res.error && res.data) {
				const title = res.data.title || '';
				const body = res.data.body || '';
				setGeneratedTitle(title);
				setGeneratedBody(body);
				
				// 履歴に保存
				const updatedHistory = addHistory({
					characterPrompt,
					imageUrls,
					bodyLength,
					generatedTitle: title,
					generatedBody: body
				});
				setHistory(updatedHistory);
			} else {
				setError('ブログ内容の生成に失敗しました');
			}
		} catch (error) {
			setError('ブログ内容の生成に失敗しました');
		} finally {
			setLoading(false);
		}
	};

	const handleCopy = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
		} catch (err) {
			// フォールバック: 古いブラウザ対応
			const textArea = document.createElement('textarea');
			textArea.value = text;
			document.body.appendChild(textArea);
			textArea.select();
			document.execCommand('copy');
			document.body.removeChild(textArea);
		}
	};

	const handleReset = () => {
		setImageUrls([]);
		setGeneratedTitle('');
		setGeneratedBody('');
		setError('');
	};

	const handleSaveCharacterPrompt = () => {
		const ok = saveCharacterPrompt(characterPrompt, shop.id);
		setPromptSaveMessage(ok ? '一時保存しました！' : '保存に失敗しました…');
	};
	
	const handleDeleteHistory = (id: string) => {
		// お気に入りの場合は削除できない
		const item = history.find(h => h.id === id);
		if (item?.isFavorite) {
			return;
		}
		const updatedHistory = deleteHistory(id);
		setHistory(updatedHistory);
	};
	
	const handleToggleFavorite = (id: string) => {
		const updatedHistory = toggleFavorite(id);
		setHistory(updatedHistory);
	};
	
	const handleLoadHistory = (item: BlogGenerationHistory) => {
		setCharacterPrompt(item.characterPrompt);
		setImageUrls(item.imageUrls);
		setBodyLength(item.bodyLength);
		setGeneratedTitle(item.generatedTitle);
		setGeneratedBody(item.generatedBody);
		setError('');
		setSelectedHistory(null);
		// 生成結果セクションまでスクロール
		setTimeout(() => {
			const resultSection = document.getElementById('generated-result-section');
			if (resultSection) {
				resultSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		}, 100);
	};
	
	const handleViewHistoryDetail = (item: BlogGenerationHistory) => {
		setSelectedHistory(item);
	};
	
	const formatDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleString('ja-JP', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	return (
		<div>
			<Box pb={3}>
				<Typography mt={2} mb={3} sx={{ fontSize: ".9rem", fontWeight: "bold" }}>
					AIブログ生成（β版）
				</Typography>
				<Typography variant="body2" color="text.secondary" mb={3}>
					・ブログ記事からAIの性格設定ができます。<br />
					・アップロードされた画像から、AIがブログのタイトル・本文を自動生成します。<br />
					・生成履歴を保存しておくことができます。<br />
					Googleマイビジネスやホットペッパービューティーへの投稿にご活用ください。
				</Typography>

				{/* 1. AIの性格を設定 */}
				<Paper elevation={1} sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" mb={2}>
						1. AIの性格を設定
					</Typography>
					<Box mb={2}>
						<Box display="flex" alignItems="center" gap={2} mb={1}>
							<Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
								性格・口調のプロンプト
							</Typography>
							<Button
								variant="contained"
								size="small"
								startIcon={<SaveIcon />}
								onClick={handleSaveCharacterPrompt}
							>
								一時保存
							</Button>
							<Button
								variant="outlined"
								size="small"
								startIcon={<PsychologyIcon />}
								onClick={() => setUrlPromptDialogOpen(true)}
							>
								ブログURLから生成
							</Button>
							<Button
								variant="outlined"
								size="small"
								startIcon={<PsychologyIcon />}
								onClick={() => setHotpepperPromptDialogOpen(true)}
							>
								Hot Pepperブログから生成
							</Button>
						</Box>
						<Box
							component="textarea"
							value={characterPrompt}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCharacterPrompt(e.target.value)}
							placeholder="例：明るく親しみやすい口調で、専門的な知識も交えながら丁寧に説明する。絵文字は使わず、読者に寄り添うような文章スタイル。"
							sx={{
								width: '100%',
								minHeight: '200px',
								padding: '16.5px 14px',
								fontSize: '0.875rem',
								lineHeight: '1.6',
								whiteSpace: 'pre-wrap',
								wordBreak: 'break-word',
								border: '1px solid rgba(0, 0, 0, 0.23)',
								borderRadius: '4px',
								resize: 'vertical',
								overflow: 'auto',
								backgroundColor: 'background.paper',
								color: 'text.primary',
								'&:focus': {
									outline: 'none',
									border: '2px solid #1976d2'
								},
								'&:hover': {
									borderColor: 'text.primary'
								},
								'&::placeholder': {
									color: 'text.secondary',
									opacity: 0.42
								}
							}}
						/>
						<Typography variant="caption" color="text.secondary" display="block" mt={1}>
							AIの性格や口調を設定します。空白でも構いませんが、設定することでより一貫性のあるブログが生成されます。
						</Typography>
						{promptSaveMessage && (
							<Typography variant="caption" color="success.main" display="block" mt={0.5}>
								{promptSaveMessage}
							</Typography>
						)}
					</Box>
				</Paper>

				{/* 2. 画像をアップロード */}
				<Paper elevation={1} sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" mb={2}>
						2. 画像をアップロード（最大4枚）
					</Typography>
					{imageUrls.length > 0 ? (
						<Box>
							<Box display="flex" flexWrap="wrap" gap={2} mb={2}>
								{imageUrls.map((url, index) => (
									<Box key={index} position="relative">
										<img
											src={url}
											alt={`アップロードされた画像${index + 1}`}
											style={{
												width: '200px',
												height: '200px',
												objectFit: 'cover',
												borderRadius: '8px'
											}}
										/>
										<Button
											variant="outlined"
											color="error"
											size="small"
											onClick={() => handleDeleteImage(index)}
											sx={{
												position: 'absolute',
												top: 8,
												right: 8,
												minWidth: 'auto',
												width: 32,
												height: 32,
												borderRadius: '50%',
												backgroundColor: 'rgba(255, 255, 255, 0.9)'
											}}
										>
											×
										</Button>
									</Box>
								))}
							</Box>
							<Box display="flex" gap={2}>
								<Button
									variant="outlined"
									onClick={handleReset}
									size="small"
								>
									画像を変更
								</Button>
								{imageUrls.length < 4 && (
									<S3Uploader
										shop_id={shop.id}
										onComplete={handleImageUpload}
										btn_text="画像を追加"
										size_limit={1024 * 1024 * 10} // 10MB
										directory={`ai-blog/${shop.id}`}
									/>
								)}
							</Box>
						</Box>
					) : (
						<S3Uploader
							shop_id={shop.id}
							onComplete={handleImageUpload}
							btn_text="画像をアップロード"
							size_limit={1024 * 1024 * 10} // 10MB
							directory={`ai-blog/${shop.id}`}
						/>
					)}
				</Paper>

				{/* 3. 生成設定 */}
				<Paper elevation={1} sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" mb={2}>
						3. 生成設定
					</Typography>
					<FormControl sx={{ minWidth: 200 }}>
						<InputLabel>文字数</InputLabel>
						<Select
							value={bodyLength}
							label="文字数"
							onChange={(e) => setBodyLength(e.target.value as number)}
						>
							{LENGTH_OPTIONS.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Paper>

				{/* 4. ブログ内容を生成 */}
				<Paper elevation={1} sx={{ p: 3, mb: 3 }}>
					<Typography variant="h6" mb={2}>
						4. ブログ内容を生成
					</Typography>
					<LoadingButton
						variant="contained"
						size="large"
						startIcon={<AutoAwesomeIcon />}
						onClick={handleGenerate}
						loading={loading}
						disabled={imageUrls.length === 0}
					>
						AIでブログ内容を生成
					</LoadingButton>

					{/* エラー表示 */}
					{error && (
						<Alert severity="error" sx={{ mt: 2 }}>
							{error}
						</Alert>
					)}

					{/* 生成結果 */}
					{(generatedTitle || generatedBody) && (
						<Box mt={3} id="generated-result-section">
							<Typography variant="h6" mb={2}>
								生成された内容
							</Typography>
							<Typography variant="body2" color="red" mb={2}>
								誤った内容が生成される可能性があります。ご確認のうえ投稿にご使用ください。
							</Typography>
							<Paper elevation={1} sx={{ p: 2, mb: 2 }}>
								<Box display="flex" alignItems="center" mb={1}>
									<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
										タイトル
									</Typography>
									<Tooltip title="タイトルをコピー">
										<IconButton
											onClick={() => handleCopy(generatedTitle)}
											size="small"
										>
											<ContentCopyIcon />
										</IconButton>
									</Tooltip>
								</Box>
								<Typography variant="body1" mb={2}>
									{generatedTitle}
								</Typography>

								<Box display="flex" alignItems="center" mb={1}>
									<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
										本文
									</Typography>
									<Tooltip title="本文をコピー">
										<IconButton
											onClick={() => handleCopy(generatedBody)}
											size="small"
										>
											<ContentCopyIcon />
										</IconButton>
									</Tooltip>
								</Box>
								<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
									{generatedBody}
								</Typography>
							</Paper>
						</Box>
					)}
				</Paper>

				{/* 5. 生成履歴 */}
				{history.length > 0 && (
					<Paper elevation={1} sx={{ p: 3, mb: 3 }} id="blog-history-section">
						<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
							<Box display="flex" alignItems="center">
								<HistoryIcon sx={{ mr: 1 }} />
								<Typography variant="h6">
									生成履歴 ({history.length}件)
								</Typography>
							</Box>
							<Typography variant="caption" color="text.secondary">
								最大{MAX_HISTORY_COUNT}件まで保存されます
							</Typography>
						</Box>
						<Grid container spacing={2}>
							{history.map((item) => (
								<Grid item xs={12} sm={6} md={4} key={item.id}>
									<Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
										<CardContent sx={{ flexGrow: 1 }}>
											<Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
												<Box display="flex" alignItems="center" gap={1}>
													{item.isFavorite && (
														<FavoriteIcon sx={{ color: 'error.main', fontSize: '1rem' }} />
													)}
													<Typography variant="caption" color="text.secondary">
														{formatDate(item.createdAt)}
													</Typography>
												</Box>
												<Chip 
													label={`${item.bodyLength}文字`} 
													size="small" 
													color="primary" 
													variant="outlined"
												/>
											</Box>
											<Typography variant="subtitle1" fontWeight="bold" mb={1} noWrap>
												{item.generatedTitle || '（タイトルなし）'}
											</Typography>
											<Typography 
												variant="body2" 
												color="text.secondary" 
												sx={{ 
													mb: 1,
													display: '-webkit-box',
													WebkitLineClamp: 3,
													WebkitBoxOrient: 'vertical',
													overflow: 'hidden'
												}}
											>
												{item.generatedBody}
											</Typography>
											<Box display="flex" alignItems="center" gap={0.5} mt={1}>
												<ImageIcon fontSize="small" color="action" />
												<Typography variant="caption" color="text.secondary">
													{item.imageUrls.length}枚の画像
												</Typography>
											</Box>
											{item.characterPrompt && (
												<Chip 
													label="プロンプト設定あり" 
													size="small" 
													variant="outlined"
													sx={{ mt: 1 }}
												/>
											)}
										</CardContent>
										<CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
											<Button 
												size="small" 
												onClick={() => handleViewHistoryDetail(item)}
											>
												詳細を見る
											</Button>
											<Box display="flex" gap={0.5}>
												<Tooltip title={item.isFavorite ? "お気に入りを解除" : "お気に入りに追加"}>
													<IconButton 
														size="small" 
														color={item.isFavorite ? "error" : "default"}
														onClick={() => handleToggleFavorite(item.id)}
													>
														{item.isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
													</IconButton>
												</Tooltip>
												<Tooltip title={item.isFavorite ? "お気に入りは削除できません" : "削除"}>
													<span>
														<IconButton 
															size="small" 
															color="error"
															disabled={item.isFavorite || false}
															onClick={() => handleDeleteHistory(item.id)}
														>
															<DeleteIcon />
														</IconButton>
													</span>
												</Tooltip>
											</Box>
										</CardActions>
									</Card>
								</Grid>
							))}
						</Grid>
					</Paper>
				)}
			</Box>

			{/* プロンプト生成ダイアログ */}
			<PromptGeneratorDialog
				open={urlPromptDialogOpen}
				onClose={() => setUrlPromptDialogOpen(false)}
				shop_id={shop.id}
				onPromptGenerated={(prompt) => {
					setCharacterPrompt(prompt);
					setUrlPromptDialogOpen(false);
				}}
			/>

			<HotPepperPromptGeneratorDialog
				open={hotpepperPromptDialogOpen}
				onClose={() => setHotpepperPromptDialogOpen(false)}
				shop_id={shop.id}
				hpb_salon_id={shop.hpb_salon_id}
				onPromptGenerated={(prompt) => {
					setCharacterPrompt(prompt);
					setHotpepperPromptDialogOpen(false);
				}}
			/>

			{/* 履歴詳細ダイアログ */}
			<Dialog
				open={selectedHistory !== null}
				onClose={() => setSelectedHistory(null)}
				maxWidth="md"
				fullWidth
				PaperProps={{
					sx: {
						maxHeight: '90vh'
					}
				}}
			>
				{selectedHistory && (
					<>
						<DialogTitle>
							生成履歴の詳細
							<Typography variant="caption" color="text.secondary" display="block" mt={1}>
								{formatDate(selectedHistory.createdAt)}
							</Typography>
						</DialogTitle>
						<DialogContent sx={{ overflowY: 'auto' }}>
							<Box>
								{/* 生成結果 */}
								<Typography variant="h6" mb={2} mt={1}>
									生成結果
								</Typography>
								<Paper elevation={1} sx={{ p: 2, mb: 2 }}>
									<Box display="flex" alignItems="center" mb={1}>
										<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
											タイトル
										</Typography>
										<Tooltip title="タイトルをコピー">
											<IconButton
												onClick={() => handleCopy(selectedHistory.generatedTitle)}
												size="small"
											>
												<ContentCopyIcon />
											</IconButton>
										</Tooltip>
									</Box>
									<Typography variant="body1" mb={2}>
										{selectedHistory.generatedTitle || '（タイトルなし）'}
									</Typography>
									<Box display="flex" alignItems="center" mb={1}>
										<Typography variant="subtitle1" fontWeight="bold" sx={{ flexGrow: 1 }}>
											本文
										</Typography>
										<Tooltip title="本文をコピー">
											<IconButton
												onClick={() => handleCopy(selectedHistory.generatedBody)}
												size="small"
											>
												<ContentCopyIcon />
											</IconButton>
										</Tooltip>
									</Box>
									<Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
										{selectedHistory.generatedBody}
									</Typography>
								</Paper>

								{/* 使用した画像 */}
								<Typography variant="h6" mb={2}>
									使用した画像 ({selectedHistory.imageUrls.length}枚)
								</Typography>
								<Box display="flex" flexWrap="wrap" gap={2} mb={2}>
									{selectedHistory.imageUrls.map((url, index) => (
										<img
											key={index}
											src={url}
											alt={`使用した画像${index + 1}`}
											style={{
												width: '150px',
												height: '150px',
												objectFit: 'cover',
												borderRadius: '8px'
											}}
										/>
									))}
								</Box>

								{/* 生成設定 */}
								<Typography variant="h6" mb={2}>
									生成設定
								</Typography>
								<Box mb={2}>
									<Chip 
										label={`文字数: ${selectedHistory.bodyLength}文字`} 
										color="primary" 
										variant="outlined"
										sx={{ mr: 1 }}
									/>
								</Box>

								{/* 使用したプロンプト */}
								{selectedHistory.characterPrompt && (
									<>
										<Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
											<Typography variant="h6">
												使用したプロンプト
											</Typography>
											<Tooltip title="プロンプトをコピー">
												<IconButton
													onClick={() => handleCopy(selectedHistory.characterPrompt)}
													size="small"
												>
													<ContentCopyIcon />
												</IconButton>
											</Tooltip>
										</Box>
										<Paper elevation={1} sx={{ p: 2 }}>
											<Typography 
												variant="body2" 
												sx={{ 
													whiteSpace: 'pre-wrap',
													fontFamily: 'monospace',
													fontSize: '0.875rem'
												}}
											>
												{selectedHistory.characterPrompt}
											</Typography>
										</Paper>
									</>
								)}
							</Box>
						</DialogContent>
						<DialogActions>
							<Button onClick={() => setSelectedHistory(null)}>
								閉じる
							</Button>
							<Button 
								variant="contained"
								onClick={() => {
									handleLoadHistory(selectedHistory);
									setSelectedHistory(null);
								}}
							>
								この履歴を使用
							</Button>
						</DialogActions>
					</>
				)}
			</Dialog>
		</div>
	);
}
